<script setup lang="ts">
	import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
	import BaseDialog from "@common-ui/components/BaseDialog.vue";
	import { onBeforeRouteLeave } from "vue-router";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import SeanIcon from "@/components/icons/SeanIcon.vue";
	import { confirmDiscardChanges, type DiscardDialogText } from "@/composables/useUnsavedChangesGuard";
	import { createScribePendingUploadOwner, tryAbandonScribePendingDocumentUpload } from "@/db/scribePendingUploadDb";
	import { logError, logWarn } from "@/helpers/logError";
	import { useAuthSessionStore } from "@/stores/authSession";
	import SparkGuideEditor from "./SparkGuideEditor.vue";
	import SparkGuideReviewCompleteDialog from "./SparkGuideReviewCompleteDialog.vue";
	import type { KnowledgeAssignment, KnowledgeRequirement, PackKnowledgeFulfillment, SparkGuideDraft } from "./types";
	import { couldNot } from "@/helpers/userMessages";

	const props = withDefaults(
		defineProps<{
			modelValue: boolean;
			requirements: KnowledgeRequirement[];
			assignments?: KnowledgeAssignment[];
			includeDefaultFulfillments?: boolean;
			installing?: boolean;
		}>(),
		{
			assignments: () => [],
			includeDefaultFulfillments: true,
			installing: false
		}
	);

	const emit = defineEmits<{
		"update:modelValue": [value: boolean];
		cancel: [];
		finish: [fulfillments: PackKnowledgeFulfillment[]];
	}>();

	const display = useDisplay();
	const toast = useToast();
	const authSession = useAuthSessionStore();
	const instance = getCurrentInstance();
	const unsavedExitDialogText: DiscardDialogText = {
		title: "You have unsaved changes",
		message: "Leaving now will discard your Spark Guide changes.",
		agreeText: "Leave without saving",
		cancelText: "Keep editing"
	};
	const current = ref(0);
	// `current` is the ordinal the stepper and header render from; `activeGuideId` is which guide
	// the user is actually on. LSP-17280 was caused by treating the ordinal AS the identity, so the
	// reconciliation below must not repeat that: it keys off this id, never off the index. Retained
	// (not derived from the previous list) so it survives the list transiently emptying, and moved
	// through setActiveStep() so the two can never drift apart.
	const activeGuideId = ref<string | null>(props.requirements[0]?.id ?? null);
	// Declared up here with the step state rather than beside the other dialog refs: the guide-list
	// reconciliation watcher below tears these down when the active guide disappears, so keeping
	// them above it avoids a temporal-dead-zone ReferenceError if that watcher ever becomes
	// `immediate` and runs during setup.
	const unsavedConfirmVisible = ref(false);
	// The parked closure travels WITH the guide it is heading for. Kept in one object rather than two
	// variables so they cannot drift apart — the recurring defect in this whole area has been two
	// pieces of state that must agree being stored separately. targetGuideId is null for the arrows,
	// which resolve their destination at fire time and so have nothing to invalidate.
	let pendingNavigation: { run: () => void; targetGuideId: string | null } | null = null;
	// Single per-guide state: "reviewed". That Looks Good, Save, and Save & Continue
	// all set it (green step + counted in the summary); Revert to Original clears it.
	const reviewed = ref<Set<string>>(new Set());
	const fulfillments = ref<Map<string, PackKnowledgeFulfillment>>(new Map());
	const drafts = ref<Map<string, SparkGuideDraft>>(new Map());
	const forceDefaultGuideIds = ref<Set<string>>(new Set());
	const confirmVisible = ref(false);
	const editorRef = ref<InstanceType<typeof SparkGuideEditor> | null>(null);
	const stepperBarRef = ref<HTMLElement | null>(null);
	const editorSaving = ref(false);
	const editorBusy = ref(false);
	const editorUploading = ref(false);

	const guides = computed(() => props.requirements);
	const activeGuide = computed(() => guides.value[current.value]);
	const assignmentByRequirementId = computed(
		() => new Map((props.assignments ?? []).map((assignment) => [assignment.taskKnowledgeRequirementId, assignment]))
	);
	const activeDraft = computed(() => (activeGuide.value ? (drafts.value.get(activeGuide.value.id) ?? null) : null));
	const activeAssignment = computed(() =>
		activeGuide.value ? (assignmentByRequirementId.value.get(activeGuide.value.id) ?? null) : null
	);
	const activeForceDefaultData = computed(() => (activeGuide.value ? forceDefaultGuideIds.value.has(activeGuide.value.id) : false));
	const isLast = computed(() => current.value === guides.value.length - 1);
	const dialogWidth = computed(() => (display.smAndDown.value ? "96%" : 1120));
	const dialogHeight = computed(() => (display.smAndDown.value ? "calc(100dvh - 24px)" : "720px"));
	const dialogMaxHeight = computed(() => (display.smAndDown.value ? "calc(100dvh - 24px)" : "calc(100dvh - 64px)"));
	// Counted against the guides that actually exist. `reviewed` is keyed by guide id and is
	// deliberately NOT pruned when a guide leaves the list: the list can transiently empty and
	// repopulate, and pruning would wipe real progress. The raw set size can therefore exceed
	// guides.length, which made the completion dialog overcount against its `:total`. Departed
	// guides already contribute nothing to submission — onConfirmContinue() maps over guides.value.
	const reviewedCount = computed(() => guides.value.filter((guide) => reviewed.value.has(guide.id)).length);
	const controlsDisabled = computed(() => props.installing || editorSaving.value || editorBusy.value);
	const editorActionsDisabled = computed(() => props.installing || editorSaving.value);
	const exitControlsDisabled = computed(() => props.installing || editorSaving.value || editorUploading.value);
	const resetUserScopedWizardState = () => {
		current.value = 0;
		activeGuideId.value = guides.value[0]?.id ?? null;
		unsavedConfirmVisible.value = false;
		pendingNavigation = null;
		reviewed.value = new Set();
		fulfillments.value = new Map();
		drafts.value = new Map();
		forceDefaultGuideIds.value = new Set();
		confirmVisible.value = false;
	};

	watch(
		() => props.modelValue,
		(open) => {
			if (open) {
				resetUserScopedWizardState();
				editorBusy.value = false;
				editorUploading.value = false;
			}
		}
	);

	watch(
		() => [authSession.organizationId, authSession.userId] as const,
		([organizationId, userId], [previousOrganizationId, previousUserId]) => {
			if (organizationId === previousOrganizationId && userId === previousUserId) return;
			// Wizard drafts and staged fulfillments are memory-only and cannot be attributed after an
			// account change. Clear them synchronously before the editor's auth-triggered reload can
			// reapply a prior user's draft. Owner-scoped durable rows remain untouched for that owner.
			resetUserScopedWizardState();
		},
		{ flush: "sync" }
	);

	// Keeps the rendered ordinal and the retained guide identity in lockstep. When the target index
	// holds no guide (a transiently empty list) the last known id is deliberately kept rather than
	// nulled, so repopulating the list can still put the user back on their guide.
	function setActiveStep(requestedIndex: number) {
		// Clamped HERE rather than at each call site, because this is the only place `current` is
		// assigned and the in-range invariant has to hold for every caller. jump() in particular bakes
		// its index into the closure it hands to guardNavigation() at call time, so an unsaved-changes
		// dialog can hold that closure open while the guide list shrinks underneath it; firing it
		// unclamped afterwards would clobber the reconciliation below with a stale out-of-range index
		// and reproduce LSP-17280 with no log at all (ls-heroku-ci, round 3).
		const index = Math.min(Math.max(requestedIndex, 0), Math.max(guides.value.length - 1, 0));
		if (index !== requestedIndex) {
			logWarn("Spark Guides step request was out of range and got clamped", {
				requestedIndex,
				nextIndex: index,
				guideCount: guides.value.length
			});
		}

		current.value = index;
		const guideAtIndex = guides.value[index];
		if (guideAtIndex) {
			activeGuideId.value = guideAtIndex.id;
		}
	}

	// LSP-17280: `guides` mirrors a live prop — ScribeFab and TemplateDetailsPage both reassign
	// their own `guideRequirements` ref (including to []) independently of this dialog's
	// visibility, and `current` is otherwise only reset when the dialog opens. A list that changed
	// underneath a stale ordinal is what produced the reported state: the header printed "6." from
	// `current + 1` while the stepper, which only renders guides that actually exist, highlighted
	// step 4 — or nothing at all, because no `index === current` chip was left to match.
	//
	// Gated on modelValue because both consumers clear their requirements to [] as part of closing;
	// without the gate every ordinary cancel/finish from step 2+ would log an anomaly and bury the
	// real incidents this logging exists to catch.
	watch(guides, (nextGuides) => {
		if (!props.modelValue) return;

		const previousIndex = current.value;
		const previousActiveId = activeGuideId.value;
		const clampedIndex = Math.min(Math.max(previousIndex, 0), Math.max(nextGuides.length - 1, 0));

		if (previousActiveId === null) {
			// Reachable when the dialog opens before its requirements load: activeGuideId is seeded from
			// the first requirement, so it is null for an empty list and the first populate lands here.
			// No identity to preserve yet, and setActiveStep() owns the bounds invariant.
			setActiveStep(previousIndex);
			return;
		}

		// A parked jump whose destination has left the list has to be cancelled even when the active
		// guide is untouched. Otherwise Discard would still run revertToOriginal() and throw away the
		// user's in-progress edits, and then the closure would no-op — destroying work to buy a
		// navigation that never happens (Bugbot, round 7).
		// Captured before any teardown below, because two of them can fire in the same run: if the
		// active guide AND the parked target both leave in one update, computing this after the
		// cancellation would report false for a prompt that was really discarded (Bugbot, round 8).
		const promptWasArmed = unsavedConfirmVisible.value || pendingNavigation !== null;

		const parkedTargetGuideId = pendingNavigation?.targetGuideId ?? null;
		if (parkedTargetGuideId !== null && !nextGuides.some((guide) => guide.id === parkedTargetGuideId)) {
			logWarn("Spark Guides parked navigation cancelled — its target left the guide list", {
				targetGuideId: parkedTargetGuideId,
				activeGuideId: previousActiveId,
				guideCount: nextGuides.length
			});
			unsavedConfirmVisible.value = false;
			pendingNavigation = null;
		}

		const remappedIndex = nextGuides.findIndex((guide) => guide.id === previousActiveId);
		if (remappedIndex !== -1) {
			if (remappedIndex !== previousIndex) {
				logWarn("Spark Guides active step followed its guide after the guide list changed", {
					previousIndex,
					nextIndex: remappedIndex,
					guideId: previousActiveId,
					guideCount: nextGuides.length
				});
			}
			setActiveStep(remappedIndex);
			return;
		}

		// The guide being edited is gone. Log unconditionally — keying this on the index changing
		// would miss the worst case, where a different guide slides into the same index and the user
		// is silently moved onto someone else's content with no diagnostic trail at all.
		// The unsaved-changes prompt and the deferred navigation behind it both belonged to the guide
		// that just disappeared. Leaving them armed lets onUnsavedSave() read the guide that got
		// clamped into its place and save/mark-reviewed a guide the user never edited — before
		// setActiveStep() clamped, an out-of-range step made that same save fail closed instead
		// (Bugbot, round 4). Tear them down with the guide they describe.
		unsavedConfirmVisible.value = false;
		pendingNavigation = null;

		logWarn("Spark Guides active guide left the guide list", {
			discardedPendingPrompt: promptWasArmed,
			previousIndex,
			// nextIndex, not clampedIndex: all three of these messages describe "the index we ended
			// on" and must be correlatable by one field name.
			nextIndex: clampedIndex,
			previousGuideId: previousActiveId,
			nextGuideId: nextGuides[clampedIndex]?.id ?? null,
			guideCount: nextGuides.length
		});
		setActiveStep(clampedIndex);
	});

	// Should be unreachable now that setActiveStep() clamps: an open dialog with guides but no
	// resolvable active guide means `current` is out of range, and the v-if header guard would
	// render that as a silently blank pane. Log it rather than let it be invisible.
	watch(
		[() => props.modelValue, activeGuide, guides],
		([open, guide, list]) => {
			if (!open || !(list as KnowledgeRequirement[]).length || guide) return;
			logWarn("Spark Guides has guides but no active guide — step is out of range", {
				current: current.value,
				activeGuideId: activeGuideId.value,
				guideCount: (list as KnowledgeRequirement[]).length
			});
		},
		{ flush: "post" }
	);

	const guideHasDefaultData = (guide: KnowledgeRequirement) => {
		if (typeof guide.hasDefaultData === "boolean") {
			return guide.hasDefaultData;
		}

		return guide.defaultData != null || Boolean(guide.defaultDataS3Key);
	};

	const defaultFulfillment = (guide: KnowledgeRequirement): PackKnowledgeFulfillment | null => {
		if (!guideHasDefaultData(guide)) return null;

		return {
			requirementId: guide.id,
			knowledgeType: guide.knowledgeType,
			useDefaultData: true
		};
	};

	const stepClass = (index: number, id: string) => {
		return {
			"stepper-step--active": index === current.value,
			"stepper-step--reviewed": reviewed.value.has(id),
			"stepper-step--pending": index !== current.value && !reviewed.value.has(id)
		};
	};

	async function keepActiveStepVisible() {
		if (!props.modelValue) return;

		await nextTick();
		if (!props.modelValue) return;

		const stepperBar = stepperBarRef.value;
		if (!stepperBar) {
			logWarn("Spark Guides active step could not be scrolled", {
				reason: "stepper not mounted",
				current: current.value,
				guideId: activeGuide.value?.id ?? null
			});
			return;
		}

		const activeStep = stepperBar.querySelector<HTMLElement>(".stepper-step--active");
		if (!activeStep) {
			logWarn("Spark Guides active step could not be scrolled", {
				reason: "active step not found",
				current: current.value,
				guideCount: guides.value.length,
				guideId: activeGuide.value?.id ?? null
			});
			return;
		}

		activeStep.scrollIntoView({ block: "nearest", inline: "nearest" });
	}

	watch(
		current,
		() => {
			void keepActiveStepVisible().catch((error) => {
				logError("Spark Guides active step could not be kept visible", error, {
					current: current.value,
					guideId: activeGuide.value?.id ?? null
				});
			});
		},
		{ flush: "post" }
	);

	function markGuideReviewed(guideId: string) {
		reviewed.value.add(guideId);
	}

	async function clearCurrentGuide() {
		const guide = activeGuide.value;
		if (!guide) return;
		if (!(await abandonActiveEditorPendingState())) {
			toast.error("We couldn't discard this Spark Guide safely. Please keep this window open and try again.");
			return;
		}

		fulfillments.value.delete(guide.id);
		reviewed.value.delete(guide.id);
		drafts.value.delete(guide.id);
		forceDefaultGuideIds.value.delete(guide.id);
	}

	// Revert must restore the guide's TRUE original. After a save the editor's local
	// baseline is the saved rows, so a local restore would keep the edits on screen while
	// the staged fulfillment (and reviewed flag) are cleared underneath them.
	async function onEditorRevert() {
		const guide = activeGuide.value;
		if (!guide) return;
		if (!(await abandonActiveEditorPendingState())) {
			toast.error("We couldn't discard this Spark Guide safely. Please keep this window open and try again.");
			return;
		}

		const defaultGuideFulfillment = defaultFulfillment(guide);
		if (defaultGuideFulfillment) {
			fulfillments.value.set(guide.id, defaultGuideFulfillment);
			forceDefaultGuideIds.value.add(guide.id);
		} else {
			fulfillments.value.delete(guide.id);
			forceDefaultGuideIds.value.delete(guide.id);
		}
		reviewed.value.delete(guide.id);
		drafts.value.delete(guide.id);
		await nextTick();
		await editorRef.value?.reloadData?.();
	}

	// Enabled when either the persisted assignment or this wizard session contains something
	// revertible. A previously uploaded guide must remain revertible when the wizard is reopened;
	// limiting this to session-local edits left LSP-19288's existing customization stuck.
	const revertDisabled = computed(() => {
		const guide = activeGuide.value;
		if (!guide) return true;
		const staged = fulfillments.value.get(guide.id);
		const hasPersistedCustomization = activeAssignment.value?.useDefaultData === false && !activeForceDefaultData.value;
		return (
			!hasPersistedCustomization &&
			!reviewed.value.has(guide.id) &&
			!(staged && !staged.useDefaultData) &&
			!editorRef.value?.hasChanges
		);
	});

	function storeCurrentDraft(guideId: string, editor = editorRef.value) {
		const draft = editor?.getDraft?.();
		if (!draft) return;

		drafts.value.set(guideId, draft);
	}

	async function captureCurrent() {
		const guide = activeGuide.value;
		const editor = editorRef.value;
		if (!guide || !editor) return null;
		const ownerOrganizationId = authSession.organizationId;
		const ownerUserId = authSession.userId;
		const editRevision = editor.getEditRevision?.();

		const built = await editor.buildFulfillment();
		if (authSession.organizationId !== ownerOrganizationId || authSession.userId !== ownerUserId) {
			logWarn("SparkGuidesWizard: discarded save completion after the authenticated owner changed", {
				savingGuideId: guide.id
			});
			return undefined;
		}
		if (built) {
			if (activeGuide.value?.id !== guide.id || editorRef.value !== editor || editor.getEditRevision?.() !== editRevision) {
				console.warn("SparkGuidesWizard: discarded save completion after the active editor changed", {
					savingGuideId: guide.id,
					currentGuideId: activeGuide.value?.id ?? null
				});
				return undefined;
			}
			fulfillments.value.set(guide.id, built);
			forceDefaultGuideIds.value.delete(guide.id);
			editor.markSaved?.(built);
			storeCurrentDraft(guide.id, editor);
			return built;
		} else if (!fulfillments.value.has(guide.id)) {
			const defaultGuideFulfillment = defaultFulfillment(guide);
			if (defaultGuideFulfillment) {
				fulfillments.value.set(guide.id, defaultGuideFulfillment);
				return defaultGuideFulfillment;
			}
		}

		return fulfillments.value.get(guide.id) ?? null;
	}

	// Unsaved-changes confirmation: editor state dies on the per-step :key remount, so
	// navigating away from ANY dirty guide would silently discard the edits — typed text
	// for text guides, row edits for tables.
	const unsavedSaving = ref(false);

	const hasUnsavedChanges = () => Boolean(editorRef.value?.hasChanges);
	const hasStagedGuideChanges = () =>
		Array.from(fulfillments.value.values()).some((fulfillment) => !fulfillment.useDefaultData) || drafts.value.size > 0;
	const hasUnsavedGuideExit = () => props.modelValue && (hasUnsavedChanges() || hasStagedGuideChanges());
	const clearPendingUploadWithRetry = async (requirementId: string) => {
		const pendingUploadOwner = createScribePendingUploadOwner(authSession.organizationId, authSession.userId);
		return pendingUploadOwner ? tryAbandonScribePendingDocumentUpload(requirementId, pendingUploadOwner) : true;
	};
	const abandonActiveEditorPendingState = async () => {
		const activeRequirementId = activeGuide.value?.id;
		if (!activeRequirementId) return true;
		if (editorRef.value?.abandonPendingState) {
			try {
				return await editorRef.value.abandonPendingState();
			} catch (error) {
				logWarn("SparkGuidesWizard: editor abandonPendingState rejected unexpectedly", {
					requirementId: activeRequirementId,
					error
				});
				return false;
			}
		}
		return clearPendingUploadWithRetry(activeRequirementId);
	};
	const clearSessionPendingDocumentUploads = async () => {
		const requirementIds = new Set([...drafts.value.keys(), ...fulfillments.value.keys()]);
		const activeRequirementId = activeGuide.value?.id;
		if (activeRequirementId) requirementIds.delete(activeRequirementId);
		const activeCleared = await abandonActiveEditorPendingState();
		const otherResults = await Promise.all(Array.from(requirementIds, clearPendingUploadWithRetry));
		if (activeCleared && otherResults.every(Boolean)) return true;
		toast.error("We couldn't discard all Spark Guide changes safely. Please keep this window open and try again.");
		return false;
	};

	function getBlockedGuideExitReason() {
		if (!props.modelValue) return null;
		if (props.installing) return "installation";
		if (editorSaving.value) return "save";
		if (editorUploading.value) return "file upload";
		return null;
	}

	function notifyBlockedGuideExit(surface: "route navigation" | "browser unload") {
		const reason = getBlockedGuideExitReason();
		if (!reason) return false;

		logWarn("Spark Guides exit blocked while an operation is in progress", { surface, reason });
		if (surface === "route navigation") {
			toast.warning("Please wait for your Spark Guide changes to finish before leaving.");
		}
		return true;
	}

	function handleBeforeUnload(event: BeforeUnloadEvent) {
		const blockedExit = notifyBlockedGuideExit("browser unload");
		if (!hasUnsavedGuideExit() && !blockedExit) return;
		event.preventDefault();
		// Some browsers still require this legacy fallback before showing their native prompt.
		event.returnValue = "Unsaved changes";
	}

	onBeforeRouteLeave(async () => {
		if (notifyBlockedGuideExit("route navigation")) return false;
		if (!hasUnsavedGuideExit()) return;
		try {
			const discard = await confirmDiscardChanges(instance?.proxy, unsavedExitDialogText);
			if (!discard) return false;
			if (!(await clearSessionPendingDocumentUploads())) return false;
			return;
		} catch (error) {
			logError("SparkGuidesWizard.onBeforeRouteLeave: confirmDiscardChanges failed", error);
			toast.error(couldNot("show the exit confirmation"));
			return false;
		}
	});

	onMounted(() => {
		window.addEventListener("beforeunload", handleBeforeUnload);
	});

	onBeforeUnmount(() => {
		window.removeEventListener("beforeunload", handleBeforeUnload);
	});

	function guardNavigation(navigate: () => void, targetGuideId: string | null = null) {
		if (hasUnsavedChanges()) {
			pendingNavigation = { run: navigate, targetGuideId };
			unsavedConfirmVisible.value = true;
			return;
		}
		navigate();
	}

	function onUnsavedCancel() {
		unsavedConfirmVisible.value = false;
		pendingNavigation = null;
	}

	async function onUnsavedDiscard() {
		// Same hazard as onUnsavedSave, and worse: revertToOriginal() below acts on whatever guide
		// editorRef has been remounted onto, so an unguarded late click silently throws away a
		// different guide's edits.
		if (!unsavedPromptIsArmed("onUnsavedDiscard")) return;

		const navigate = pendingNavigation?.run;
		unsavedSaving.value = true;
		// Visibly drop the discarded edits — on the last step there is no remount to do it,
		// so without this the editor keeps showing edits the wizard will never submit.
		const abandoned = await abandonActiveEditorPendingState();
		unsavedSaving.value = false;
		if (!abandoned) return;
		unsavedConfirmVisible.value = false;
		pendingNavigation = null;
		navigate?.();
	}

	// Both prompt buttons share this: the prompt is torn down when its guide leaves the list, and a
	// late click on either must not act on whichever guide is active now. Logged, because a real
	// late click is otherwise an invisible no-op.
	function unsavedPromptIsArmed(handler: string): boolean {
		if (unsavedConfirmVisible.value) return true;
		logWarn("Spark Guides unsaved-changes action ignored — its prompt is no longer armed", {
			handler,
			activeGuideId: activeGuideId.value
		});
		return false;
	}

	async function onUnsavedSave() {
		if (unsavedSaving.value || controlsDisabled.value) return;
		if (!unsavedPromptIsArmed("onUnsavedSave")) return;

		const guide = activeGuide.value;
		const navigate = pendingNavigation?.run;
		unsavedSaving.value = true;
		try {
			if (!(await saveCurrentWithGuard())) return;
		} finally {
			unsavedSaving.value = false;
		}

		// Re-checked AFTER the await, not just on entry: saveCurrentWithGuard() is genuinely async, so
		// the guide-list reconciliation can run mid-flight. These are TWO independent questions, and
		// conflating them cost a review round (Bugbot, round 8) — gating both on the prompt flag meant
		// a cancelled NAVIGATION also silently threw away the review credit for a save that had
		// already persisted.
		//
		// 1. Is the guide we saved still the active one? If not, marking it reviewed would attribute
		//    the user's work to the wrong guide, so abandon.
		if (activeGuideId.value !== guide?.id) {
			logWarn("Spark Guides unsaved-changes save landed after its guide stopped being active", {
				savedGuideId: guide?.id ?? null,
				activeGuideId: activeGuideId.value
			});
			return;
		}

		// The save itself is valid, so the step turns green whether or not we can still navigate.
		if (guide) markGuideReviewed(guide.id);

		// 2. Is the navigation still valid? Reconciliation nulls pendingNavigation when its
		//    destination leaves the list, which invalidates the move but not the save.
		const navigationStillValid = pendingNavigation !== null;
		unsavedConfirmVisible.value = false;
		pendingNavigation = null;
		if (!navigationStillValid) {
			logWarn("Spark Guides save kept its review credit but its navigation was already cancelled", {
				savedGuideId: guide?.id ?? null
			});
			return;
		}
		navigate?.();
	}

	function goNext() {
		if (controlsDisabled.value) return;
		guardNavigation(() => {
			if (isLast.value) {
				confirmVisible.value = true;
				return;
			}
			setActiveStep(current.value + 1);
		});
	}

	function goPrev() {
		if (controlsDisabled.value) return;
		if (current.value === 0) return;
		guardNavigation(() => {
			setActiveStep(current.value - 1);
		});
	}

	function jump(index: number) {
		if (controlsDisabled.value) return;
		if (index === current.value) return;

		const targetGuideId = guides.value[index]?.id;
		if (!targetGuideId) {
			logWarn("Spark Guides jump ignored — no guide at the requested step", {
				requestedIndex: index,
				guideCount: guides.value.length
			});
			return;
		}

		guardNavigation(() => {
			// Resolved at FIRE time by id, never by the index captured at click time. guardNavigation()
			// can park this closure behind the unsaved-changes prompt while the list reorders underneath
			// it; replaying a raw index would land the user on whatever guide moved into that slot, which
			// is in range and so would pass the clamp silently — the exact "moved onto someone else's
			// content" failure this ticket exists to prevent (ls-heroku-ci/AI review, round 5).
			const targetIndex = guides.value.findIndex((guide) => guide.id === targetGuideId);
			if (targetIndex === -1) {
				logWarn("Spark Guides deferred jump target left the guide list", {
					targetGuideId,
					guideCount: guides.value.length
				});
				return;
			}
			setActiveStep(targetIndex);
		}, targetGuideId);
	}

	async function saveCurrent(): Promise<boolean> {
		const guide = activeGuide.value;
		if (!guide) return false;
		try {
			const fulfillment = await captureCurrent();
			if (fulfillment === undefined) return false;
			return true;
		} catch (error) {
			console.error("Failed to save Spark Guide edits:", { guideId: guide.id, error });
			toast.error(couldNot("save your Spark Guide edits"));
			return false;
		}
	}

	async function saveCurrentWithGuard(): Promise<boolean> {
		if (editorSaving.value) return false;

		editorSaving.value = true;
		try {
			return await saveCurrent();
		} finally {
			editorSaving.value = false;
		}
	}

	// The inline Save button: saving a guide counts as reviewing it — the step turns green.
	async function saveAndMarkReviewed() {
		if (controlsDisabled.value) return;
		const guide = activeGuide.value;
		if (!guide) return;
		if (!editorRef.value?.hasChanges) {
			toast.info("No changes detected. This Spark Guide is already up to date.");
			return;
		}
		if (!(await saveCurrentWithGuard())) return;
		markGuideReviewed(guide.id);
	}

	function onEditorBusy(value: boolean) {
		editorBusy.value = value;
	}

	function onEditorUploading(value: boolean) {
		editorUploading.value = value;
	}

	function markReviewed() {
		if (controlsDisabled.value) return;
		const guide = activeGuide.value;
		if (!guide) return;
		guardNavigation(() => {
			markGuideReviewed(guide.id);
			if (isLast.value) {
				confirmVisible.value = true;
				return;
			}
			setActiveStep(current.value + 1);
		});
	}

	async function onConfirmContinue() {
		if (props.includeDefaultFulfillments !== false) {
			for (const guide of guides.value) {
				if (!fulfillments.value.has(guide.id)) {
					const defaultGuideFulfillment = defaultFulfillment(guide);
					if (defaultGuideFulfillment) {
						fulfillments.value.set(guide.id, defaultGuideFulfillment);
					}
				}
			}
		}
		confirmVisible.value = false;
		const selectedFulfillments = guides.value
			.map((guide) => fulfillments.value.get(guide.id))
			.filter((fulfillment): fulfillment is PackKnowledgeFulfillment => Boolean(fulfillment));
		emit("finish", selectedFulfillments);
	}

	function onModelValueUpdate(value: boolean) {
		if (!value && exitControlsDisabled.value) return;
		emit("update:modelValue", value);
	}

	async function onCancel() {
		if (exitControlsDisabled.value) return;
		if (hasUnsavedGuideExit()) {
			try {
				const discard = await confirmDiscardChanges(instance?.proxy, unsavedExitDialogText);
				if (!discard) return;
				if (!(await clearSessionPendingDocumentUploads())) return;
			} catch (error) {
				logError("SparkGuidesWizard.onCancel: confirmDiscardChanges failed", error);
				toast.error(couldNot("show the exit confirmation"));
				return;
			}
		}
		emit("update:modelValue", false);
		emit("cancel");
	}

	defineExpose({
		goNext,
		goPrev,
		jump,
		saveCurrent,
		saveAndMarkReviewed,
		markReviewed,
		onConfirmContinue,
		onUnsavedCancel,
		onUnsavedDiscard,
		onUnsavedSave,
		unsavedConfirmVisible,
		fulfillments,
		reviewedCount,
		clearCurrentGuide
	});
</script>

<template>
	<v-dialog
		:model-value="modelValue"
		:width="dialogWidth"
		max-width="1120"
		persistent
		scrollable
		@update:model-value="onModelValueUpdate">
		<v-card
			class="spark-guides-wizard-card d-flex flex-column"
			:height="dialogHeight"
			:max-height="dialogMaxHeight"
			rounded="xl"
			elevation="8">
			<div class="d-flex align-center ga-4 pa-4 border-b">
				<div class="d-flex flex-column align-center ga-1 flex-shrink-0">
					<SeanIcon :enable-tooltip="false" :height="56" :width="56" />
					<span class="text-caption font-weight-bold">Spark Guides</span>
				</div>
				<div ref="stepperBarRef" class="stepper-bar flex-grow-1" data-test-id="spark-guides-stepper">
					<div class="d-flex align-center ga-1 stepper-scroll">
						<template v-for="(guide, index) in guides" :key="guide.id">
							<div
								class="stepper-step d-flex align-center ga-2 px-3 py-2 rounded"
								:data-test-id="`spark-guide-step-${index}`"
								:class="stepClass(index, guide.id)"
								:aria-disabled="controlsDisabled"
								@click="jump(index)">
								<v-icon
									size="16"
									:color="reviewed.has(guide.id) ? 'success' : index === current ? 'primary' : 'grey-lighten-1'">
									{{ reviewed.has(guide.id) ? "mdi-check-circle" : "mdi-file-document-outline" }}
								</v-icon>
								<span class="stepper-step__number text-caption font-weight-bold">{{ index + 1 }}</span>
								<span class="stepper-step__label text-caption font-weight-medium">{{ guide.name }}</span>
							</div>
							<v-icon v-if="index < guides.length - 1" size="18" class="stepper-separator mx-0 flex-shrink-0">
								mdi-chevron-right
							</v-icon>
						</template>
					</div>
				</div>
			</div>

			<div class="wizard-body pa-6 flex-grow-1 d-flex flex-column">
				<h6 v-if="activeGuide" class="text-h6 mb-1 flex-shrink-0">{{ current + 1 }}. {{ activeGuide.name }}</h6>
				<p v-if="activeGuide" class="text-body-2 text-medium-emphasis mb-4 flex-shrink-0">{{ activeGuide.description }}</p>
				<SparkGuideEditor
					v-if="activeGuide"
					ref="editorRef"
					:key="activeGuide.id"
					:requirement="activeGuide"
					:assignment="activeAssignment"
					:draft="activeDraft"
					:force-default-data="activeForceDefaultData"
					inline-revert-mode="request"
					:inline-revert-disabled="revertDisabled"
					:actions-disabled="editorActionsDisabled"
					@busy="onEditorBusy"
					@uploading="onEditorUploading"
					@revert="onEditorRevert">
					<template #actions-end="{ hasChanges, isBinaryDoc }">
						<!-- Binary docs (pdf/docx) have nothing to save until a replacement file is
						uploaded, so Save appears only once there are staged changes. -->
						<SecondaryBtn
							v-if="!isBinaryDoc || hasChanges"
							:loading="editorSaving"
							:disabled="controlsDisabled"
							@click="saveAndMarkReviewed">
							Save
						</SecondaryBtn>
					</template>
				</SparkGuideEditor>
			</div>

			<div class="d-flex align-center ga-3 pa-4 border-t">
				<TertiaryBtn :disabled="exitControlsDisabled" @click="onCancel">Cancel</TertiaryBtn>
				<v-btn
					color="success"
					variant="flat"
					rounded="xl"
					prepend-icon="mdi-check"
					:disabled="controlsDisabled"
					@click="markReviewed">
					That looks good
				</v-btn>
				<v-spacer />
				<v-btn
					data-test-id="spark-guides-previous-button"
					icon
					variant="flat"
					color="primary"
					:disabled="controlsDisabled || current === 0"
					@click="goPrev">
					<v-icon>mdi-chevron-left</v-icon>
				</v-btn>
				<v-btn
					data-test-id="spark-guides-next-button"
					icon
					variant="flat"
					color="primary"
					:loading="installing && isLast"
					:disabled="controlsDisabled"
					@click="goNext">
					<v-icon>{{ isLast ? "mdi-check" : "mdi-chevron-right" }}</v-icon>
				</v-btn>
			</div>
		</v-card>

		<SparkGuideReviewCompleteDialog
			v-model="confirmVisible"
			:reviewed-count="reviewedCount"
			:total="guides.length"
			@go-back="confirmVisible = false"
			@continue="onConfirmContinue" />

		<BaseDialog v-model="unsavedConfirmVisible" title="Save your changes?" size="sm" persistent actions-layout="between">
			You've made edits to this Spark Guide that haven't been saved. Would you like to save them before moving on?
			<template #actions>
				<TertiaryBtn :disabled="unsavedSaving" @click="onUnsavedCancel">Cancel</TertiaryBtn>
				<v-spacer />
				<v-btn color="warning" variant="outlined" rounded="xl" :disabled="unsavedSaving" @click="onUnsavedDiscard">
					Continue without saving
				</v-btn>
				<PrimaryBtn :loading="unsavedSaving" @click="onUnsavedSave">Save & Continue</PrimaryBtn>
			</template>
		</BaseDialog>
	</v-dialog>
</template>

<style scoped lang="scss">
	.border-b {
		border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	}

	.border-t {
		border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
	}

	.stepper-bar {
		min-width: 0;
		overflow-x: auto;
	}

	.stepper-scroll {
		width: max-content;
		max-width: none;
		padding: 6px;
		border-radius: 8px;
		background-color: rgba(var(--v-theme-on-surface), 0.02);
	}

	.stepper-step {
		flex: 0 1 220px;
		min-width: 140px;
		max-width: 260px;
		cursor: pointer;
		border: 1px solid transparent;
		color: rgba(var(--v-theme-on-surface), 0.88);
		transition:
			background-color 0.15s ease,
			border-color 0.15s ease,
			box-shadow 0.15s ease,
			opacity 0.15s ease;
		user-select: none;
	}

	.stepper-step--active {
		background-color: rgba(var(--v-theme-primary), 0.14);
		border-color: rgba(var(--v-theme-primary), 0.55);
		box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.1);
	}

	.stepper-step--reviewed {
		background-color: rgba(var(--v-theme-success), 0.16);
		border-color: rgba(var(--v-theme-success), 0.16);
		color: rgba(var(--v-theme-on-surface), 0.88);
	}

	.stepper-step--active.stepper-step--reviewed {
		background-color: rgba(var(--v-theme-success), 0.14);
		border-color: rgba(var(--v-theme-primary), 0.55);
		box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.1);
	}

	.stepper-step--pending {
		opacity: 0.68;
	}

	.stepper-step:hover {
		background-color: rgba(var(--v-theme-primary), 0.06);
		opacity: 1;
	}

	.stepper-step__number {
		color: rgba(var(--v-theme-on-surface), 0.72);
	}

	.stepper-step__label {
		min-width: 0;
		color: rgba(var(--v-theme-on-surface), 0.82);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.stepper-step--pending .stepper-step__number,
	.stepper-step--pending .stepper-step__label {
		color: rgba(var(--v-theme-on-surface), 0.52);
	}

	.stepper-step--active .stepper-step__number,
	.stepper-step--active .stepper-step__label {
		color: rgba(var(--v-theme-on-surface), 0.9);
	}

	.stepper-separator {
		color: rgba(var(--v-theme-on-surface), 0.34);
	}

	.wizard-body {
		min-height: 0;
		overflow: hidden;
	}
</style>
